import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// import { Link } from 'react-router-dom'; // Removed unused import
import styles from './QuestsPage.module.css';
import { useLives } from '../context/LivesContext';
import { useAuth } from '../context/AuthContext';
import { addXP } from '../utils/questUtils';
// import UserHeader from '../components/UserHeader';
import Quest, { QuestData } from '../components/Quest';
import InstructionCard from '../components/InstructionCard';
import LivesDisplay from '../components/LivesDisplay';
import { useQuest } from '../hooks/useQuest';
import { formatTime } from '../utils/questUtils';
import { QUEST_DATA, COLORS } from '../constants';
import BottomNavBar from '../components/BottomNavBar';
import PageContent from '../components/PageContent';
import plaidService from '../services/plaidService';
import TrackSpendingModal from '../components/TrackSpendingModal';
import TopSpendingCategoryModal from '../components/TopSpendingCategoryModal';
import YearlyIncomeModal from '../components/YearlyIncomeModal';
import SpendingGuessModal from '../components/SpendingGuessModal';
import OnboardingModal from '../components/OnboardingModal';

const QuestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { addLives } = useLives();
  const { isNewUser, hasCompletedOnboarding, completeOnboarding } = useAuth();
  const eatingOutQuest = useQuest('eating-out');
  const savingsQuest = useQuest('savings');
  const emergencyFundQuest = useQuest('emergency-fund');
  const weeklyCheckInQuest = useQuest('weekly-check-in');
  const financialGuruQuest = useQuest('financial-guru');
  const trackSpendingQuest = useQuest('track-spending');
  const topSpendingCategoryQuest = useQuest('top-spending-category');
  const yearlyIncomeQuest = useQuest('yearly-income');
  const spendingGuessQuest = useQuest('spending-guesstimate');
  const [emergencyFundGoal, setEmergencyFundGoal] = useState<string | null>(null);
  const [hasBankAccount, setHasBankAccount] = useState<boolean>(false);
  const [isTrackSpendingModalOpen, setTrackSpendingModalOpen] = useState<boolean>(false);
  const [isTopSpendingCategoryModalOpen, setTopSpendingCategoryModalOpen] = useState<boolean>(false);
  const [isYearlyIncomeModalOpen, setYearlyIncomeModalOpen] = useState<boolean>(false);
  const [isSpendingGuessModalOpen, setSpendingGuessModalOpen] = useState<boolean>(false);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);

  // Auto-complete weekly check-in quest when returning from transactions page
  useEffect(() => {
    if (weeklyCheckInQuest.isStarted) {
      console.log('[QuestsPage] Weekly check-in quest is started, auto-completing...');
      weeklyCheckInQuest.completeQuest();
      addLives(7); // Add 7 lives for completing the weekly check-in quest
    }
  }, [weeklyCheckInQuest.isStarted, weeklyCheckInQuest, addLives]);

  useEffect(() => {
    const goal = localStorage.getItem('emergency_fund_goal_amount');
    setEmergencyFundGoal(goal);
  }, []);

  // Show onboarding for new users who haven't completed it
  useEffect(() => {
    if (isNewUser && !hasCompletedOnboarding) {
      // Small delay to allow page to render first
      setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
    }
  }, [isNewUser, hasCompletedOnboarding]);

  // Check if user has connected bank account
  const checkBankConnection = useCallback(async () => {
    try {
      const transactionData = await plaidService.getPersistedTransactions();
      setHasBankAccount((transactionData || []).length > 0);
    } catch (error) {
      console.error('Error checking bank connection:', error);
      setHasBankAccount(false);
    }
  }, []);

  useEffect(() => {
    checkBankConnection();
  }, [checkBankConnection]);



  const handleCompleteSavings = () => {
    savingsQuest.resetQuest();
    addLives(3); // Add 3 lives for completing the savings quest
    addXP(50); // Award 50 XP for completing a quest
  };

  const handleGiveUpSavings = () => {
    savingsQuest.resetQuest();
    // Clear the timer from localStorage
    localStorage.removeItem(`quest_timer_savings`);
  };

  const handleCompleteEmergencyFund = () => {
    emergencyFundQuest.completeQuest();
    addLives(10); // Add 10 lives for completing the emergency fund quest
    addXP(50); // Award 50 XP for completing a quest
  };

  const handleCompleteWeeklyCheckIn = () => {
    weeklyCheckInQuest.completeQuest();
    addLives(7); // Add 7 lives for completing the weekly check-in quest
    addXP(50); // Award 50 XP for completing a quest
  };

  const handleCompleteFinancialGuru = () => {
    financialGuruQuest.completeQuest();
    addLives(5); // Add 5 lives for completing the financial guru quest
    addXP(50); // Award 50 XP for completing a quest
  };

  const handleStartTrackSpending = () => {
    setTrackSpendingModalOpen(true);
  };

  const handleCompleteTrackSpending = (amount: number) => {
    trackSpendingQuest.completeQuest();
    addLives(3); // Add 3 lives for completing the track spending quest
    addXP(50); // Award 50 XP for completing a quest
    setTrackSpendingModalOpen(false);
    console.log('User tracked spending amount:', amount);
    
    // Navigate to games page after quest completion
    setTimeout(() => navigate('/games'), 500);
  };

  const handleStartTopSpendingCategory = () => {
    setTopSpendingCategoryModalOpen(true);
  };

  const handleCompleteTopSpendingCategory = (category: string) => {
    topSpendingCategoryQuest.completeQuest();
    addLives(4); // Add 4 lives for completing the top spending category quest
    addXP(50); // Award 50 XP for completing a quest
    setTopSpendingCategoryModalOpen(false);
    console.log('User identified top spending category:', category);
    
    // Navigate to games page after quest completion
    setTimeout(() => navigate('/games'), 500);
  };

  const handleStartYearlyIncome = () => {
    setYearlyIncomeModalOpen(true);
  };

  const handleCompleteYearlyIncome = (income: number) => {
    yearlyIncomeQuest.completeQuest();
    addLives(5); // Add 5 lives for completing the yearly income quest
    addXP(50); // Award 50 XP for completing a quest
    setYearlyIncomeModalOpen(false);
    console.log('User entered yearly income:', income);

    // Navigate to games page after quest completion
    setTimeout(() => navigate('/games'), 500);
  };

  const handleStartSpendingGuess = () => {
    setSpendingGuessModalOpen(true);
  };

  const handleCompleteSpendingGuess = (selectedRange: string) => {
    spendingGuessQuest.completeQuest();
    addLives(3); // Add 3 lives for completing the spending guess quest
    addXP(50); // Award 50 XP for completing a quest
    setSpendingGuessModalOpen(false);
    console.log('User selected spending range:', selectedRange);

    // Navigate to games page after quest completion
    setTimeout(() => navigate('/games'), 500);
  };

  // Onboarding handlers
  const handleOnboardingComplete = (selectedGoal: string) => {
    completeOnboarding();
    setShowOnboarding(false);
    console.log('Onboarding completed with goal:', selectedGoal);
  };

  const handleOnboardingSkip = () => {
    completeOnboarding();
    setShowOnboarding(false);
    console.log('Onboarding skipped');
  };

  // Define all quests
  const allQuests: QuestData[] = [
    {
      ...QUEST_DATA.EATING_OUT,
      isStarted: eatingOutQuest.isStarted,
      timeRemaining: eatingOutQuest.timeRemaining,
      progressPercentage: eatingOutQuest.progressPercentage,
      showTimer: true,
      actionButtonText: 'Give Up',
      actionButtonColor: COLORS.DANGER,
      onStart: eatingOutQuest.startQuest,
      onAction: eatingOutQuest.resetQuest
    },
    {
      ...QUEST_DATA.SAVINGS,
      isStarted: savingsQuest.isStarted,
      timeRemaining: savingsQuest.timeRemaining,
      progressPercentage: savingsQuest.progressPercentage,
      showTimer: false,
      actionButtonText: savingsQuest.isStarted ? 'Give Up' : 'Complete',
      actionButtonColor: savingsQuest.isStarted ? COLORS.DANGER : COLORS.SUCCESS,
      onStart: savingsQuest.startQuest,
      onAction: savingsQuest.isStarted ? handleGiveUpSavings : handleCompleteSavings
    },
    {
      ...QUEST_DATA.EMERGENCY_FUND,
      isStarted: emergencyFundQuest.isStarted,
      timeRemaining: emergencyFundQuest.timeRemaining,
      progressPercentage: emergencyFundQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Complete',
      actionButtonColor: COLORS.SUCCESS,
      onStart: emergencyFundQuest.startQuest,
      onAction: handleCompleteEmergencyFund
    },
    {
      ...QUEST_DATA.WEEKLY_CHECK_IN,
      isStarted: weeklyCheckInQuest.isStarted,
      timeRemaining: weeklyCheckInQuest.timeRemaining,
      progressPercentage: weeklyCheckInQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Complete',
      actionButtonColor: COLORS.SUCCESS,
      onStart: weeklyCheckInQuest.startQuest,
      onAction: handleCompleteWeeklyCheckIn
    },
    {
      ...QUEST_DATA.FINANCIAL_GURU,
      isStarted: financialGuruQuest.isStarted,
      timeRemaining: financialGuruQuest.timeRemaining,
      progressPercentage: financialGuruQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Complete',
      actionButtonColor: COLORS.SUCCESS,
      onStart: financialGuruQuest.startQuest,
      onAction: handleCompleteFinancialGuru
    },
    {
      ...QUEST_DATA.TRACK_SPENDING,
      isStarted: trackSpendingQuest.isStarted,
      timeRemaining: trackSpendingQuest.timeRemaining,
      progressPercentage: trackSpendingQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Start',
      actionButtonColor: COLORS.PRIMARY,
      onStart: handleStartTrackSpending,
      onAction: handleStartTrackSpending
    },
    {
      ...QUEST_DATA.TOP_SPENDING_CATEGORY,
      isStarted: topSpendingCategoryQuest.isStarted,
      timeRemaining: topSpendingCategoryQuest.timeRemaining,
      progressPercentage: topSpendingCategoryQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Start',
      actionButtonColor: COLORS.PRIMARY,
      onStart: handleStartTopSpendingCategory,
      onAction: handleStartTopSpendingCategory
    },
    {
      ...QUEST_DATA.YEARLY_INCOME,
      isStarted: yearlyIncomeQuest.isStarted,
      timeRemaining: yearlyIncomeQuest.timeRemaining,
      progressPercentage: yearlyIncomeQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Start',
      actionButtonColor: COLORS.PRIMARY,
      onStart: handleStartYearlyIncome,
      onAction: handleStartYearlyIncome
    },
    {
      ...QUEST_DATA.SPENDING_GUESSTIMATE,
      isStarted: spendingGuessQuest.isStarted,
      timeRemaining: spendingGuessQuest.timeRemaining,
      progressPercentage: spendingGuessQuest.progressPercentage,
      showTimer: false,
      actionButtonText: 'Start',
      actionButtonColor: COLORS.PRIMARY,
      onStart: handleStartSpendingGuess,
      onAction: handleStartSpendingGuess
    }
  ];

  // Filter out quests that require bank account connection if user hasn't connected one
  const bankAccountRequiredQuests = ['eating-out', 'savings', 'weekly-check-in'];
  const quests: QuestData[] = allQuests.filter(quest => {
    if (bankAccountRequiredQuests.includes(quest.id) && !hasBankAccount) {
      return false;
    }
    return true;
  });

  // Debug logging for Financial Guru quest
  console.log('[QuestsPage] Financial Guru quest state:', {
    isStarted: financialGuruQuest.isStarted,
    timeRemaining: financialGuruQuest.timeRemaining,
    progressPercentage: financialGuruQuest.progressPercentage
  });

  // Debug logging for Emergency Fund quest
  console.log('[QuestsPage] Emergency Fund quest state:', {
    isStarted: emergencyFundQuest.isStarted,
    timeRemaining: emergencyFundQuest.timeRemaining,
    progressPercentage: emergencyFundQuest.progressPercentage
  });

  // Debug logging for all quests
  console.log('[QuestsPage] Quest states:', {
    'eating-out': {
      isStarted: eatingOutQuest.isStarted,
      timeRemaining: eatingOutQuest.timeRemaining,
      progressPercentage: eatingOutQuest.progressPercentage
    },
    'savings': {
      isStarted: savingsQuest.isStarted,
      timeRemaining: savingsQuest.timeRemaining,
      progressPercentage: savingsQuest.progressPercentage
    },
    'emergency-fund': {
      isStarted: emergencyFundQuest.isStarted,
      timeRemaining: emergencyFundQuest.timeRemaining,
      progressPercentage: emergencyFundQuest.progressPercentage
    },
    'weekly-check-in': {
      isStarted: weeklyCheckInQuest.isStarted,
      timeRemaining: weeklyCheckInQuest.timeRemaining,
      progressPercentage: weeklyCheckInQuest.progressPercentage
    },
    'financial-guru': {
      isStarted: financialGuruQuest.isStarted,
      timeRemaining: financialGuruQuest.timeRemaining,
      progressPercentage: financialGuruQuest.progressPercentage
    }
  });

  return (
    <>
      <PageContent className={styles.questsPage}>
        <h1 className={styles.title}>Quests</h1>
        <LivesDisplay />
        <InstructionCard
        title={isNewUser ? "Welcome to Your Quest Journey!" : "How Quests Work"}
        content={[
          "Complete quests to earn lives and XP. Each quest teaches you a new financial skill and helps you build healthy money habits.",
          "Some quests have timers or require you to check back later. Try to complete them all!"
        ]}
        firstTimeUserContent={[
          "Welcome to WealthPlay! Quests are your pathway to financial mastery. Each one is designed to teach you valuable skills while making personal finance fun.",
          "Start with any quest that interests you! We recommend trying 'Track Spending' or 'Your Yearly Income' first - they're perfect for beginners.",
          "You earn XP (experience points) and Lives for completing quests. Lives let you play games, and XP tracks your progress toward financial goals.",
          "Don't worry about getting everything right - this is all about learning and improving your financial habits step by step!"
        ]}
        emoji="🏁"
        storageKey="quests_instructions_dismissed"
        isFirstTimeUser={isNewUser}
      />
      
      {!hasBankAccount && (
        <InstructionCard
          title={isNewUser ? "Unlock More Quests (Optional)" : "More Quests Available"}
          content={[
            "Connect your bank account to unlock additional quests including:",
            "• Don't eat out for 24 hours",
            "• Move $5 from checking to savings", 
            "• The Weekly Check-in",
            "Visit the Home page to connect your bank account and access these quests!"
          ]}
          firstTimeUserContent={[
            "Ready for more challenges? You can connect your bank account to unlock advanced quests, but it's completely optional!",
            "Bank connection allows WealthPlay to:",
            "• Track your real transactions automatically",
            "• Provide personalized spending insights",
            "• Unlock quests based on your actual spending patterns",
            "Your banking information is 100% secure and encrypted. You can always try the current quests first and connect later when you're ready!"
          ]}
          emoji="🏦"
          storageKey="bank_connection_quests_dismissed"
          isFirstTimeUser={isNewUser}
        />
      )}
      {/* Quests Section */}
      <section className={styles.questsSection}>
        <h2 className={styles.questsTitle}>Complete Quests to Earn Lives</h2>
        {quests.map((quest) => (
          <Quest 
            key={quest.id} 
            quest={quest} 
            formatTime={formatTime}
            {...(quest.id === 'emergency-fund' && emergencyFundGoal ? { emergencyFundGoal } : {})}
          />
        ))}
      </section>
      
      <TrackSpendingModal
        isOpen={isTrackSpendingModalOpen}
        onClose={() => setTrackSpendingModalOpen(false)}
        onComplete={handleCompleteTrackSpending}
      />
      
      <TopSpendingCategoryModal
        isOpen={isTopSpendingCategoryModalOpen}
        onClose={() => setTopSpendingCategoryModalOpen(false)}
        onComplete={handleCompleteTopSpendingCategory}
      />
      
      <YearlyIncomeModal
        isOpen={isYearlyIncomeModalOpen}
        onClose={() => setYearlyIncomeModalOpen(false)}
        onComplete={handleCompleteYearlyIncome}
      />

      <SpendingGuessModal
        isOpen={isSpendingGuessModalOpen}
        onClose={() => setSpendingGuessModalOpen(false)}
        onComplete={handleCompleteSpendingGuess}
      />

      {/* Onboarding Modal for first-time users */}
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
      </PageContent>

      <BottomNavBar />
    </>
  );
};

export default QuestsPage; 